/**
 * Machine Types for MEV-Med Frontend
 * Inventário de máquinas e equipamentos (Laudo NR 12) — contrato da issue #362.
 *
 * Ids seguem o padrão dos outros types do front (`number`), embora a API os entregue como string.
 */

export enum MachineStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MAINTENANCE = 'MAINTENANCE',
  DECOMMISSIONED = 'DECOMMISSIONED',
}

export type MachineProtectionState = 'CONFORME' | 'EM_DESACORDO' | 'DANIFICADA';

export type MachineAdequacyPriority = 'ALTA' | 'MEDIA' | 'BAIXA';

export type MachineAdequacyProgress = 'NAO_INICIADA' | 'EM_ANDAMENTO' | 'CONCLUIDA';

/** Situação calculada da máquina na lista (não é gravada). */
export type MachineSituation = 'EM_CADASTRO' | 'ADEQUAR' | 'CONFORME';

export const MACHINE_PROTECTION_TYPES = [
  'PROTECAO_FIXA',
  'PROTECAO_MOVEL_INTERTRAVADA',
  'CORTINA_DE_LUZ',
  'COMANDO_BIMANUAL',
  'TAPETE_SENSIVEL',
  'PARADA_DE_EMERGENCIA',
  'OUTRA',
] as const;

export type MachineProtectionType = (typeof MACHINE_PROTECTION_TYPES)[number];

export interface MachineProtection {
  id: number;
  machineId: number;
  protectionType: MachineProtectionType;
  /** Obrigatória quando o tipo é `OUTRA`. */
  description: string | null;
  /** Onde a proteção atua. */
  location: string | null;
  state: MachineProtectionState;
  observations: string | null;
}

export interface MachineAdequacy {
  id: number;
  machineId: number;
  hazardAssessmentId: number | null;
  description: string;
  priority: MachineAdequacyPriority;
  dueDate: string | null;
  responsible: string | null;
  progress: MachineAdequacyProgress;
}

/** Os quatro fatores da escala HRN, na ordem da multiplicação. */
export interface HrnFactors {
  likelihood: number;
  exposureFrequency: number;
  harmSeverity: number;
  peopleExposed: number;
}

export interface MachineHazardAssessment extends HrnFactors {
  id: number;
  machineId: number;
  hazard: string;
  /** Calculados e gravados no servidor. */
  hrn: number;
  hrnLevel: string;
  scaleVersion: string;
  /** Fatores "após adequações": nulos até serem informados. */
  residualLikelihood: number | null;
  residualExposureFrequency: number | null;
  residualHarmSeverity: number | null;
  residualPeopleExposed: number | null;
  residualHrn: number | null;
  residualLevel: string | null;
}

export interface Machine {
  id: number;
  companyId: number;
  departmentId: number | null;
  sourceGeneratorId: number | null;
  name: string;
  tag: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  yearOfManufacture: number | null;
  location: string | null;
  status: MachineStatus;
  maintenanceFrequency: string | null;
  lastMaintenanceDate: string | null;
  nextMaintenanceDate: string | null;
  observations: string | null;
  protections?: MachineProtection[];
  adequacies?: MachineAdequacy[];
  hazardAssessments?: MachineHazardAssessment[];
}

/** Linha da lista: a máquina mais o resumo calculado pelo servidor. */
export interface MachineListItem extends Machine {
  protectionCount: number;
  openAdequacyCount: number;
  /** Nível do perigo de maior HRN; nulo quando nenhum perigo foi avaliado. */
  worstHrnLevel: string | null;
  /** Maior HRN entre os perigos; nulo quando nenhum perigo foi avaliado. */
  worstHrn: number | null;
  situation: MachineSituation;
}

export interface MachineFilters {
  search?: string;
  departmentId?: number;
  status?: MachineStatus;
  /** Nível de risco (nome do nível da escala HRN). */
  hrnLevel?: string;
  /** Nome do nível da escala HRN: máquinas cujo pior perigo é este nível ou pior. */
  hrnLevelMin?: string;
  /** Só as máquinas sem nenhum perigo avaliado. */
  semApreciacao?: boolean;
}

export interface MachineListResponse {
  machines: MachineListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type MachineFormData = Pick<Machine, 'name'> &
  Partial<Omit<Machine, 'id' | 'companyId' | 'name' | 'protections' | 'adequacies' | 'hazardAssessments'>>;

/** Criação: quem não tem empresa ativa (plataforma, clínica) diz a empresa no corpo. */
export type MachineCreateData = MachineFormData & { companyId?: number };

export type MachineProtectionFormData = Omit<MachineProtection, 'id' | 'machineId'>;

export type MachineAdequacyFormData = Omit<MachineAdequacy, 'id' | 'machineId'>;

/** O servidor calcula `hrn`, `hrnLevel`, `scaleVersion` e os residuais calculados. */
export type MachineHazardAssessmentFormData = HrnFactors & {
  hazard: string;
  residualLikelihood?: number | null;
  residualExposureFrequency?: number | null;
  residualHarmSeverity?: number | null;
  residualPeopleExposed?: number | null;
};

export interface HrnScaleFactor {
  /** Nome do campo em `HrnFactors`. */
  key: keyof HrnFactors;
  label: string;
  values: number[];
  /** Cada valor com a descrição mostrada no seletor. */
  options: { value: number; label: string }[];
}

export interface HrnScaleBand {
  /** HRN até este valor (inclusive); `null` na faixa aberta "acima". */
  upTo: number | null;
  level: string;
}

/** Resposta de `GET /api/machines/hrn-scale`. */
export interface HrnScale {
  version: string;
  factors: HrnScaleFactor[];
  bands: HrnScaleBand[];
}
